import { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Send, Loader2, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Message {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export default function DirectChat() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [threadId, setThreadId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom whenever messages change (on load AND on new messages)
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load or create thread on mount
  useEffect(() => {
    loadOrCreateThread();
  }, []);

  const loadOrCreateThread = async () => {
    try {
      setIsLoadingHistory(true);
      
      // Try to get the user's existing thread
      const { data: existingThreads, error: fetchError } = await supabase
        .from('agent_control_threads')
        .select('id')
        .order('updated_at', { ascending: false })
        .limit(1);

      if (fetchError) {
        console.error('Error fetching thread:', fetchError);
        toast.error('Failed to load chat history');
        setIsLoadingHistory(false);
        return;
      }

      let currentThreadId: string;

      if (existingThreads && existingThreads.length > 0) {
        // Use existing thread
        currentThreadId = existingThreads[0].id;
      } else {
        // Create new thread (title will be auto-generated or null)
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          toast.error('Not authenticated');
          setIsLoadingHistory(false);
          return;
        }

        const { data: newThread, error: createError } = await supabase
          .from('agent_control_threads')
          .insert({
            user_id: user.id
          })
          .select('id')
          .single();

        if (createError || !newThread) {
          console.error('Error creating thread:', createError);
          toast.error('Failed to create chat thread');
          setIsLoadingHistory(false);
          return;
        }

        currentThreadId = newThread.id;
        
        // Add welcome message for new threads
        await supabase
          .from('agent_control_messages')
          .insert({
            thread_id: currentThreadId,
            role: 'assistant',
            content: "Hello! I'm Bridgette, your Level 4.5 Meta Agent. You have direct access to me here. This is your persistent system brain - I remember everything across sessions. Ask me anything about the system, your jobs, leads, or have me execute actions for you."
          });
      }

      setThreadId(currentThreadId);
      await loadMessages(currentThreadId);
    } catch (error) {
      console.error('Error in loadOrCreateThread:', error);
      toast.error('Failed to initialize chat');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const loadMessages = async (threadId: string) => {
    try {
      const { data, error } = await supabase
        .from('agent_control_messages')
        .select('id, role, content, created_at')
        .eq('thread_id', threadId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error loading messages:', error);
        toast.error('Failed to load messages');
        return;
      }

      if (data) {
        setMessages(data.map(msg => ({
          id: msg.id,
          role: msg.role as "user" | "assistant",
          content: msg.content,
          timestamp: new Date(msg.created_at)
        })));
      }
    } catch (error) {
      console.error('Error in loadMessages:', error);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || !threadId || isLoading) return;

    const messageText = input.trim();
    setInput('');
    setIsLoading(true);

    try {
      // Save user message to database first
      const { data: savedUserMsg, error: userMsgError } = await supabase
        .from('agent_control_messages')
        .insert({
          thread_id: threadId,
          role: 'user',
          content: messageText
        })
        .select('id, role, content, created_at')
        .single();

      if (userMsgError || !savedUserMsg) {
        console.error('Error saving user message:', userMsgError);
        toast.error('Failed to save message');
        setIsLoading(false);
        return;
      }

      const userMessage: Message = {
        id: savedUserMsg.id,
        role: "user",
        content: savedUserMsg.content,
        timestamp: new Date(savedUserMsg.created_at)
      };

      // Build history array including the new user message
      const history = [...messages, userMessage];
      setMessages(history);

      // Call agent
      const { data, error } = await supabase.functions.invoke('agent-control-chat', {
        body: {
          messages: history.map(m => ({
            role: m.role,
            content: m.content
          }))
        }
      });

      // Check for error in both places
      const errorMessage = data?.error || error?.message;
      const errorCode = data?.code;

      if (errorMessage) {
        // Save error as assistant message
        const errorContent = `⚠️ Error ${errorCode ? `(${errorCode})` : ''}: ${errorMessage}`;
        
        const { data: savedErrorMsg } = await supabase
          .from('agent_control_messages')
          .insert({
            thread_id: threadId,
            role: 'assistant',
            content: errorContent
          })
          .select('id, role, content, created_at')
          .single();

        if (savedErrorMsg) {
          const assistantError: Message = {
            id: savedErrorMsg.id,
            role: "assistant",
            content: savedErrorMsg.content,
            timestamp: new Date(savedErrorMsg.created_at)
          };
          setMessages(prev => [...prev, assistantError]);
        }
        
        toast.error(errorMessage);
        return;
      }

      // Success - save assistant response to database
      const replyContent = data?.response || data?.reply || "No response received from agent.";
      
      const { data: savedAssistantMsg, error: assistantMsgError } = await supabase
        .from('agent_control_messages')
        .insert({
          thread_id: threadId,
          role: 'assistant',
          content: replyContent
        })
        .select('id, role, content, created_at')
        .single();

      if (assistantMsgError || !savedAssistantMsg) {
        console.error('Error saving assistant message:', assistantMsgError);
        // Still show the message even if save failed
        const assistantMessage: Message = {
          role: "assistant",
          content: replyContent,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        const assistantMessage: Message = {
          id: savedAssistantMsg.id,
          role: "assistant",
          content: savedAssistantMsg.content,
          timestamp: new Date(savedAssistantMsg.created_at)
        };
        setMessages(prev => [...prev, assistantMessage]);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      const errorContent = `⚠️ Unexpected error: ${err instanceof Error ? err.message : 'Unknown error'}`;
      
      // Try to save error message
      if (threadId) {
        const { data: savedErrorMsg } = await supabase
          .from('agent_control_messages')
          .insert({
            thread_id: threadId,
            role: 'assistant',
            content: errorContent
          })
          .select('id, role, content, created_at')
          .single();

        if (savedErrorMsg) {
          const assistantError: Message = {
            id: savedErrorMsg.id,
            role: "assistant",
            content: savedErrorMsg.content,
            timestamp: new Date(savedErrorMsg.created_at)
          };
          setMessages(prev => [...prev, assistantError]);
        }
      }
      
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (isLoadingHistory) {
    return (
      <Card className="h-[calc(100vh-200px)]">
        <CardContent className="flex items-center justify-center h-full">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading your persistent brain...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-[calc(100vh-200px)]">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-primary" />
            <div>
              <CardTitle>Direct Communication with Bridgette</CardTitle>
              <CardDescription>
                Natural language interface - Talk directly to your Level 4.5 Meta Agent
              </CardDescription>
            </div>
          </div>
          <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
            <div className="w-2 h-2 rounded-full bg-green-500 mr-2 animate-pulse" />
            Connected {threadId ? '• Persistent' : ''}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col h-[calc(100%-120px)] gap-4">
        <ScrollArea 
          className="flex-1 rounded-lg border bg-muted/30 p-4"
        >
          <div className="space-y-4">
            {messages.map((message, idx) => (
              <div
                key={message.id || idx}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-4 ${
                    message.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-card border'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {message.role === 'assistant' && (
                      <Sparkles className="w-4 h-4 text-primary" />
                    )}
                    <span className="text-xs font-semibold">
                      {message.role === 'user' ? 'You' : 'Bridgette'}
                    </span>
                    <span className="text-xs opacity-60">
                      {message.timestamp.toLocaleTimeString()}
                    </span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-card border rounded-lg p-4 max-w-[80%]">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    <span className="text-sm text-muted-foreground">
                      Bridgette is thinking...
                    </span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        <div className="flex gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message to Bridgette... (Shift+Enter for new line)"
            disabled={isLoading || !threadId}
            className="min-h-[80px] resize-none"
          />
          <Button
            onClick={sendMessage}
            disabled={isLoading || !input.trim() || !threadId}
            className="px-6"
            size="lg"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Send className="w-5 h-5" />
                <span className="ml-2">Send</span>
              </>
            )}
          </Button>
        </div>

        <div className="text-xs text-muted-foreground">
          <MessageSquare className="w-3 h-3 inline mr-1" />
          Direct meta-level access • All capabilities enabled • Full transparency mode • Memory persists across sessions
        </div>
      </CardContent>
    </Card>
  );
}
